import React, { useEffect, useRef, useState } from 'react';
import { Chip } from '@material-ui/core';

/** Chips mounted per batch. The chip box shows ~2 rows, so one batch fills it many times over. */
export const TAG_BATCH = 100;

type ProgressiveTagsProps = {
	value: any[];
	getTagProps: (args: { index: number }) => any;
	getLabel: (option: any) => string;
};

/**
 * PR-3790: Autocomplete `renderTags` that mounts chips in batches as the chip box scrolls,
 * so "select all" on thousands of groups shows every name without mounting thousands of chips.
 */
const ProgressiveTags = ({ value, getTagProps, getLabel }: ProgressiveTagsProps) => {
	const [visibleCount, setVisibleCount] = useState<number>(TAG_BATCH);
	const sentinelRef = useRef<HTMLSpanElement>(null);
	const hasMore = value.length > visibleCount;

	// The sentinel sits after the last mounted chip; once it scrolls into view, mount the next batch.
	useEffect(() => {
		const node = sentinelRef.current;
		if (!hasMore || !node || typeof IntersectionObserver === 'undefined') return;

		const observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) {
				setVisibleCount((count) => count + TAG_BATCH);
			}
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, [hasMore, visibleCount]);

	return (
		<>
			{value.slice(0, visibleCount).map((option, index) => (
				<Chip label={getLabel(option)} {...getTagProps({ index })} />
			))}
			{hasMore && (
				<span
					ref={sentinelRef}
					aria-hidden
					style={{ display: 'inline-block', width: 1, height: 1 }}
				/>
			)}
		</>
	);
};

export default ProgressiveTags;
